<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import BriefcaseIcon from '@/assets/svg/briefcase.svg?component';
import SearchIcon from '@/assets/svg/magnifying-glass.svg?component';
import { useProjectSelector } from '@/composables/useProjectSelector';
import { formatProjectLocation } from '@/core/shared/project/project-location.util';

import type { Project } from '@factoryfixinc/ats-interfaces';

const emit = defineEmits<{
  (e: 'select', jobId: number): void;
  (e: 'close'): void;
}>();

// Unfiltered on purpose: with no status filter the backend already returns only
// non-removed (live + archived) jobs, which is exactly the "All jobs" scope.
// prioritizeCopilot mirrors the client-side sort below so server pages arrive in the
// same active-first order; myJobsFilter is driven by the header toggle.
const { projects, isLoadingProjects, fetchProjects, projectSearch, myJobsFilter } =
  useProjectSelector({ prioritizeCopilot: true });

// Snapshot of the unfiltered first page. While a query is active the composable swaps
// `projects` for server-side title/req-ID matches, so client-side filtering needs the
// full page to match against. Capturing the first settled, non-empty page exactly once
// (rather than on every `projects` change while the query reads empty) stops a stale
// search response — one that resolves *after* the query is cleared — from overwriting the
// base with a filtered subset. The empty pre-fetch list is captured but does not lock, so
// the real page still wins once it lands; an employer with genuinely no jobs never locks
// but has nothing to match against anyway.
const baseProjects = ref<Project[]>([]);
const hasInitializedBase = ref(false);
watch(
  [projects, isLoadingProjects],
  ([list, loading]) => {
    if (loading || hasInitializedBase.value || projectSearch.value.trim()) return;
    baseProjects.value = list;
    if (list.length) hasInitializedBase.value = true;
  },
  { immediate: true },
);

// Toggling "My jobs" refetches a different base set (team-scoped vs all), so release the
// one-time snapshot lock and let the next settled page re-capture as the new base —
// otherwise the dropdown keeps rendering the originally-snapshotted full list.
watch(myJobsFilter, () => {
  hasInitializedBase.value = false;
  baseProjects.value = [];
});

const searchInputRef = ref<HTMLInputElement | null>(null);

onMounted(() => {
  fetchProjects();
  searchInputRef.value?.focus();
});

function statusLabel(project: Project): string {
  return project.copilot ? 'Active' : 'Inactive';
}

function metaLine(project: Project): string {
  return [formatProjectLocation(project.location), `#${project.jobId}`, statusLabel(project)]
    .filter(Boolean)
    .join(' • ');
}

// `updateTs` is typed Date but arrives as an ISO string (the fetch path does no Date
// revival), and spec mocks may omit it — coerce and treat missing as oldest.
function updatedAt(project: Project): number {
  return project.updateTs ? new Date(project.updateTs).getTime() : 0;
}

// Active (copilot) jobs first, then most-recently-updated within each group. Mirrors
// the server's prioritizeCopilot order so the rendered list stays consistent whether a
// query is active (client merge) or not (base page).
function sortJobs(list: Project[]): Project[] {
  return [...list].sort((a, b) => {
    if (!!a.copilot !== !!b.copilot) return a.copilot ? -1 : 1;
    return updatedAt(b) - updatedAt(a);
  });
}

function matchesQuery(project: Project, query: string, idQuery: string): boolean {
  return (
    (project.title ?? '').toLowerCase().includes(query) ||
    (project.location ?? '').toLowerCase().includes(query) ||
    String(project.jobId).includes(idQuery) ||
    (project.remoteJobId ?? '').toLowerCase().includes(idQuery) ||
    // startsWith so "active" never matches Inactive by substring.
    statusLabel(project).toLowerCase().startsWith(query)
  );
}

// Hybrid search: instant client-side matches over the loaded page, unioned with the
// composable's debounced server results (title contains / req-ID exact) so employers
// with more jobs than one page still find anything by title or req ID. The predicate
// also gates the server list because `projects` lags the query by the debounce — a
// stale response would otherwise flash unrelated rows.
const displayedProjects = computed<Project[]>(() => {
  const query = projectSearch.value.trim().toLowerCase();
  if (!query) return sortJobs(baseProjects.value);

  const idQuery = query.replace(/^#/, '');
  const merged = new Map<number, Project>();
  for (const project of [...baseProjects.value, ...projects.value]) {
    if (!merged.has(project.jobId) && matchesQuery(project, query, idQuery)) {
      merged.set(project.jobId, project);
    }
  }
  return sortJobs([...merged.values()]);
});

const hasQuery = computed(() => projectSearch.value.trim().length > 0);

function pick(jobId: number): void {
  emit('select', jobId);
  emit('close');
}
</script>

<template>
  <div class="job-picker">
    <!-- Click-away backdrop so the panel closes without a global listener. -->
    <button
      type="button"
      class="jp-backdrop"
      aria-label="Close job picker"
      data-test="smart-search-job-backdrop"
      @click="emit('close')"
    />

    <div class="jp-panel" role="listbox" data-test="smart-search-job-picker">
      <div class="jp-header">
        <span class="jp-header-title">All jobs</span>
        <button
          type="button"
          class="jp-myjobs"
          :class="{ 'is-active': myJobsFilter }"
          :aria-pressed="myJobsFilter"
          data-test="smart-search-job-myjobs-toggle"
          @click="myJobsFilter = !myJobsFilter"
        >
          My jobs
        </button>
      </div>

      <div class="jp-search">
        <SearchIcon viewBox="0 0 24 24" />
        <input
          ref="searchInputRef"
          v-model="projectSearch"
          type="text"
          placeholder="Search title, location, or ID"
          aria-label="Search jobs"
          data-test="smart-search-job-search"
        />
      </div>

      <div
        v-if="isLoadingProjects && !displayedProjects.length"
        class="jp-state"
        data-test="smart-search-job-loading"
      >
        Loading jobs…
      </div>
      <div v-else-if="displayedProjects.length" class="jp-list menu-scrollbar">
        <button
          v-for="project in displayedProjects"
          :key="project.jobId"
          type="button"
          role="option"
          class="jp-item"
          data-test="smart-search-job-option"
          @click="pick(project.jobId)"
        >
          <span class="jp-icon"><BriefcaseIcon viewBox="0 0 24 24" /></span>
          <span class="jp-body">
            <span class="jp-title">{{ project.title }}</span>
            <span class="jp-meta" data-test="smart-search-job-meta">{{ metaLine(project) }}</span>
          </span>
        </button>
      </div>
      <div v-else-if="hasQuery" class="jp-state" data-test="smart-search-job-empty">
        No matching jobs found.
      </div>
      <div v-else class="jp-state" data-test="smart-search-job-empty">No jobs found.</div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.jp-backdrop {
  @apply fixed inset-0 z-40 cursor-default;
}
/* Dark dropdown anchored under the chip. On mobile it anchors LEFT and clamps to
   the viewport so a right-aligned 340px panel can't run off the left edge; on
   desktop (>=md) it keeps the right-aligned 340px placement from the prototype. */
.jp-panel {
  @apply absolute left-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-[12px] shadow-2xl;
  width: min(340px, calc(100vw - 2rem));
  background: #151728;
  border: 1px solid #252840;
}
@media (min-width: 768px) {
  .jp-panel {
    left: auto;
    right: 0;
    width: 340px;
  }
}
.jp-header {
  @apply flex items-center justify-between px-4 pb-1 pt-3;
}
.jp-header-title {
  @apply text-xs font-semibold;
  color: #7a7e9a;
}
/* Compact pill mirroring SmartSearchHeader's .mode-chip: dark by default, highlight
   purple when active (same treatment as .is-forced). */
.jp-myjobs {
  @apply inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors;
  color: #7a7e9a;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid #1e2642;
}
.jp-myjobs:hover {
  color: #f0f1f5;
  border-color: #2a3356;
  background: rgba(255, 255, 255, 0.05);
}
.jp-myjobs.is-active {
  @apply font-bold text-white;
  background: #6c40e5;
  border-color: #6c40e5;
}
/* Same field language as the header's search row, scaled to the panel. */
.jp-search {
  @apply mx-3 mb-2 mt-1 flex items-center gap-2 rounded-[8px] px-3 py-2;
  background: #141a2e;
  border: 1px solid #1e2642;
}
.jp-search:focus-within {
  border-color: rgba(108, 64, 229, 0.5);
  box-shadow: 0 0 0 3px rgba(108, 64, 229, 0.12);
}
.jp-search svg {
  @apply h-3.5 w-3.5 shrink-0;
  color: #6b7084;
}
.jp-search input {
  @apply min-w-0 flex-1 border-none bg-transparent text-[13px] outline-none;
  color: #f0f1f5;
  caret-color: #6c40e5;
}
.jp-search input::placeholder {
  color: #6b7084;
}
/* Thumb colours are the panel's own icon and meta greys, so the shared .menu-scrollbar pill
   reads on the dark panel. */
.jp-list {
  @apply max-h-[260px] overflow-y-auto py-1;
  --menu-scrollbar-thumb: #4a4d6a;
  --menu-scrollbar-thumb-hover: #6b7084;
}
.jp-item {
  @apply flex w-full items-start gap-3 px-4 py-3 text-left transition-colors;
}
.jp-item:hover {
  background: rgba(34, 197, 94, 0.06);
}
.jp-icon {
  @apply mt-0.5 shrink-0;
  color: #4a4d6a;
}
.jp-icon svg {
  @apply h-3.5 w-3.5;
}
.jp-body {
  @apply min-w-0 flex-1;
}
.jp-title {
  @apply block text-[13px] font-semibold;
  color: #f0f1f5;
}
.jp-meta {
  @apply mt-0.5 block text-[11px];
  color: #6b7084;
}
.jp-state {
  @apply px-4 py-3 text-[13px];
  color: #6b7084;
}
</style>
